/**
 * Data layer for `cap-over-delivery-cancel`. Thin typed fetch wrappers over /api/v1/cap-over-delivery-cancel.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CapOverDeliveryCancelRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListCapOverDeliveryCancelResponse {
	count: number;
	items: CapOverDeliveryCancelRow[];
}

export interface CreateCapOverDeliveryCancelBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/cap-over-delivery-cancel";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListCapOverDeliveryCancelResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCapOverDeliveryCancelResponse>(res);
}

export async function create(body: CreateCapOverDeliveryCancelBody): Promise<CapOverDeliveryCancelRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<CapOverDeliveryCancelRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const capOverDeliveryCancelQueryKeys = {
	all: ["cap-over-delivery-cancel"] as const,
	list: () => [...capOverDeliveryCancelQueryKeys.all] as const,
	detail: (id: string) => [...capOverDeliveryCancelQueryKeys.all, id] as const,
};
